import {useParams} from "react-router";
import {useFetchAllObjs} from "../../platform/hooks/PlatformTypeScriptHooks";
import {Card} from "../../components/Card";
import React, {useContext, useState} from "react";
import {IssueApiJson} from "./IssueApiJson";
import {NavLink} from "react-router-dom";
import {callApiDetachOptiselEventToIssue, useAllIssueAttachedOptiselEvents} from "./useIssueApi";
import UboldSpinner from "../../ubold-components/UboldSpinner";
import {OptiselEventTable} from "../optiselevent/OptiselEventTable";
import {HotIssueContext} from "./hotssue/HotIssueContextProvider";
import {PartyDisplay} from "./IssuePartyLines";
import {IssueAssigneeChangerModal} from "./IssueAssigneeChangerModal";
import {Protected} from "../../layout/Protected";
import {formatPolishDateTime} from "./IssueDateFormat";
import {IssueArchivedBanner} from "./IssueArchivedBanner";
import {IssueArchiveModal} from "./IssueArchiveModal";
import {IssueCloseAsCard} from "./IssueCloseAsCard";
import {useIssueArchiveActions} from "./useIssueArchiveActions";

export function ShowIssuePage() {

    const {id} = useParams();

    const issue = useFetchAllObjs<IssueApiJson>(`/api/issue/${id}`);

    const {result: events, refetch: refetchEvents} = useAllIssueAttachedOptiselEvents(id!);

    const {makeHotIssue} = useContext(HotIssueContext);
    const {archiveWithReason, finishArchiveFlow, restoreFromArchive, closeAsInquiry, closeAsOrder} = useIssueArchiveActions(id!);
    const [archiveModalOpened, setArchiveModalOpened] = useState(false);
    const [assigneeModalOpened, setAssigneeModalOpened] = useState(false);

    const unAttachEvent = (eventId: string) => {
        if (window.confirm("Czy na pewno odłączyć?")) {
            callApiDetachOptiselEventToIssue(eventId, id!).finally(() => {
                refetchEvents();
            })
        }
    }

    if (id == null) {
        throw new Error("Missing issue id");
    }
    if (issue == null) {
        return "...";
    }

    // Zarchiwizowana sprawa jest tylko do oglądania — bez akcji mutujących ("Pracuj z",
    // edycja, opiekun, zamykanie, odpinanie zdarzeń). Zdarzenia nadal można przeglądać.
    const issueActionButtons = issue.active && <div className={"d-flex gap-2"}>
        <Protected privilege={"PERM_HISTORY_AND_ISSUES_MANAGEMENT"}>
            <button className={"btn btn-sm btn-primary"} onClick={() => makeHotIssue(issue.id)}>Pracuj z</button>
        </Protected>
        <Protected privilege={"PERM_HISTORY_AND_ISSUES_MANAGEMENT"}>
            <NavLink to={"/edit-issue/" + issue.id} className={"btn btn-sm btn-secondary"}><i className={"fas fa-plus-circle"}/> Edytuj sprawę</NavLink>
        </Protected>
        <Protected privilege={"PERM_HISTORY_AND_ISSUES_MANAGEMENT"}>
            <button className={"btn btn-sm btn-danger"} onClick={() => setArchiveModalOpened(true)}><i className={"fas fa-archive"}/> Archiwizuj</button>
        </Protected>
    </div>;

    const assigneeChangeButton = issue.active && <Protected privilege={"PERM_HISTORY_AND_ISSUES_MANAGEMENT"}>
        <button className={"btn btn-sm btn-light-primary"} onClick={() => setAssigneeModalOpened(true)}>
            Zmień opiekuna
        </button>
    </Protected>;

    const fanSelectionOrderNumberByEventId: Record<string, number> = Object.fromEntries(
        issue.events
            .filter(event => event.fanSelectionOrderNumber !== null)
            .map(event => [event.id, event.fanSelectionOrderNumber as number])
    );

    const detachEventAction = issue.active ? (eventId: string) =>
        <Protected privilege={"PERM_HISTORY_AND_ISSUES_MANAGEMENT"}>
            <button className={"btn btn-sm btn-light ms-2"} onClick={() => unAttachEvent(eventId)}><i className={"fas fa-trash"}/> Odłącz</button>
        </Protected> : undefined;

    return <>
        {!issue.active && <IssueArchivedBanner issue={issue} restoreFromArchive={restoreFromArchive}/>}
        <Card header={"Sprawa: " + issue.name}>
            <div className="card-body">
                <div className="row">
                    <div className="col-md-6">
                        <div className="mb-3">
                            <strong>Utworzono przez:</strong> {issue.originalCreatorName}
                        </div>
                        <div className="mb-3">
                            <strong>Utworzono:</strong> {formatPolishDateTime(issue.creationInstant)}
                        </div>
                        <div className="mb-3">
                            <strong>Reportujący:</strong> <PartyDisplay party={issue.reporter}/>
                        </div>
                        <div className="mb-3 d-flex align-items-center gap-2 flex-wrap">
                            <span><strong>Opiekun:</strong> <PartyDisplay party={issue.assignee}/></span>
                            {assigneeChangeButton}
                        </div>
                        {issueActionButtons}
                    </div>
                    <div className="col-md-6">
                        {issue.issueReferences.wentechCaseNumber && (
                            <div className="mb-3">
                                <strong>Numer sprawy:</strong> {issue.issueReferences.wentechCaseNumber}
                            </div>
                        )}
                        {issue.issueReferences.wentechInquiryNumber && (
                            <div className="mb-3">
                                <strong>Numer zapytania:</strong> {issue.issueReferences.wentechInquiryNumber}
                            </div>
                        )}
                        {issue.issueReferences.customerOrderNumber && (
                            <div className="mb-3">
                                <strong>Numer zapytania klienta:</strong> {issue.issueReferences.customerOrderNumber}
                            </div>
                        )}
                        {issue.issueReferences.customerReferenceNumber && (
                            <div className="mb-3">
                                <strong>Numer referencyjny klienta:</strong> {issue.issueReferences.customerReferenceNumber}
                            </div>
                        )}
                        {issue.issueReferences.drawingNumber && (
                            <div className="mb-3">
                                <strong>Numer rysunku:</strong> {issue.issueReferences.drawingNumber}
                            </div>
                        )}
                        {issue.inquiryCustomerComment && (
                            <div className="mb-3">
                                <strong>Komentarz klienta do zapytania:</strong>
                                <p className={"p-3  bg-light text-gray-800 fst-italic"}>
                                    {issue.inquiryCustomerComment}
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </Card>
        {issue.active && <IssueCloseAsCard issue={issue} closeAsInquiry={closeAsInquiry} closeAsOrder={closeAsOrder}/>}
        <Card header="Przypisane zdarzenia" additionalClassNames={"mt-3"}>
            {events === "loading" ? <UboldSpinner/> : <div className={"table-responsive"}>
                <OptiselEventTable results={events} additionalActionComponents={detachEventAction} fanSelectionOrderNumberByEventId={fanSelectionOrderNumberByEventId}/>
            </div>}
        </Card>
        <Protected privilege={"PERM_HISTORY_AND_ISSUES_MANAGEMENT"}>
            <>
                <IssueArchiveModal
                    issueName={issue.name}
                    opened={archiveModalOpened}
                    onClose={() => setArchiveModalOpened(false)}
                    archiveWithReason={archiveWithReason}
                    onArchivedConfirmed={finishArchiveFlow}
                />
                <IssueAssigneeChangerModal
                    issueId={issue.id}
                    currentAssigneeId={issue.assignee?.id ?? null}
                    opened={assigneeModalOpened}
                    onClose={() => setAssigneeModalOpened(false)}
                />
            </>
        </Protected>
    </>;


}
